import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';
import { errorMessage } from '@/lib/errors';

export interface ThemePreferences {
  id?: string;
  user_id?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  card_background: string;
  text_primary: string;
  text_secondary: string;
  success_color: string;
  warning_color: string;
  error_color: string;
  info_color: string;
  dark_mode: boolean;
  border_radius: 'small' | 'medium' | 'large';
  font_size: 'small' | 'medium' | 'large';
}

const DEFAULT_THEME: ThemePreferences = {
  primary_color: '#1e1e1e',
  secondary_color: '#3a3a3a',
  accent_color: '#B8860B',
  background_color: '#ffffff',
  card_background: '#f9fafb',
  text_primary: '#111827',
  text_secondary: '#6b7280',
  success_color: '#10b981',
  warning_color: '#f59e0b',
  error_color: '#ef4444',
  info_color: '#3b82f6',
  dark_mode: false,
  border_radius: 'medium',
  font_size: 'medium',
};

export const useTheme = () => {
  const [theme, setTheme] = useState<ThemePreferences>(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Cargar tema del usuario
  useEffect(() => {
    loadTheme();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- existing stable pattern
  }, []);

  // Aplicar tema al DOM
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const loadTheme = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_theme_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        logger.error('Error loading theme:', error);
      }

      if (data) {
        setTheme(data as ThemePreferences);
      } else {
        // Crear preferencias por defecto
        await saveTheme(DEFAULT_THEME);
      }
    } catch (error) {
      logger.error('Error loading theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTheme = async (newTheme: Partial<ThemePreferences>, silent = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user logged in');

      const updatedTheme = { ...theme, ...newTheme };

      const { error } = await supabase
        .from('user_theme_preferences')
        .upsert({
          user_id: user.id,
          ...updatedTheme,
        });

      if (error) throw error;

      setTheme(updatedTheme);

      if (!silent) {
        toast({
          title: '✅ Tema guardado',
          description: 'Tus preferencias se han guardado correctamente',
        });
      }
    } catch (error) {
      logger.error('Error saving theme:', error);
      toast({
        title: '❌ Error',
        description: errorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const applyTheme = (themePrefs: ThemePreferences) => {
    const root = document.documentElement;

    // Aplicar colores
    root.style.setProperty('--color-primary', themePrefs.primary_color);
    root.style.setProperty('--color-secondary', themePrefs.secondary_color);
    root.style.setProperty('--color-accent', themePrefs.accent_color);
    root.style.setProperty('--color-background', themePrefs.background_color);
    root.style.setProperty('--color-card-bg', themePrefs.card_background);
    root.style.setProperty('--color-text-primary', themePrefs.text_primary);
    root.style.setProperty('--color-text-secondary', themePrefs.text_secondary);
    root.style.setProperty('--color-success', themePrefs.success_color);
    root.style.setProperty('--color-warning', themePrefs.warning_color);
    root.style.setProperty('--color-error', themePrefs.error_color);
    root.style.setProperty('--color-info', themePrefs.info_color);

    // Aplicar border radius
    const radiusMap = {
      small: '4px',
      medium: '8px',
      large: '16px',
    };
    root.style.setProperty('--border-radius', radiusMap[themePrefs.border_radius]);

    // Aplicar font size
    const fontSizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px',
    };
    root.style.setProperty('--font-size-base', fontSizeMap[themePrefs.font_size]);

    // Aplicar modo oscuro
    if (themePrefs.dark_mode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const resetTheme = async () => {
    await saveTheme(DEFAULT_THEME);
  };

  return {
    theme,
    loading,
    saveTheme,
    resetTheme,
  };
};
